import { useEffect, useState } from "react";
import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import type { OfferPaymentStatus } from "@shared/types/payment";
import { foyerApi } from "../../http/foyer";

/**
 * The "Demo payments" banner (`POS-8`, `docs/room-offers.md` §6, requirement 3).
 *
 * ## Why this is a component and not three copies of an `<Alert>`
 *
 * §6 requires the warning on **every surface where the demo provider is
 * active** — the offers pane, the requests inbox (POS-9) and the revenue
 * dashboard (POS-12). Three hand-rolled alerts would be three chances for one of
 * them to word it softly, to be made dismissible "just on this page", or to be
 * dropped in a refactor and never noticed, because the failure mode of a missing
 * warning is a page that looks fine.
 *
 * So it is one component reading one server verdict, and mounting it is a single
 * import line:
 *
 * ```tsx
 * import DemoPaymentsBanner from "../components/offers/DemoPaymentsBanner";
 * // …
 * <DemoPaymentsBanner />
 * ```
 *
 * ## Non-dismissible, and loud
 *
 * No `onClose`, no collapse, no "don't show again". An operator who cannot tell
 * that the money is imaginary will eventually serve a real guest with it, and a
 * banner somebody dismissed in March cannot warn them in April. §6 says "not a
 * subtle chip", so it is a full-width warning alert with a title.
 *
 * ## It renders NOTHING when payments are real AND working
 *
 * The verdict comes from the server (`GET /api/foyer/offers/payment-status`),
 * from the provider's own `demo` flag rather than from its name — a future
 * `mews-sandbox` is just as imaginary as `manual`, and a banner that only knew
 * the string "manual" would go quiet for it.
 *
 * There is a **third state** and it needed saying: a provider that is registered
 * and cannot post (`mews` / `opera` are declared and unimplemented). Charges
 * there are not imaginary, they are absent — every accepted order stays unpaid —
 * and a CMS that looked identical to a working integration would be the worse
 * of the two failures. So `operational: false` draws its own, calmer banner.
 *
 * A **failed** probe also renders nothing, deliberately: the CMS must not
 * announce "demo payments" on the strength of a network blip, and the boot log
 * plus the request rows are the authorities. The one thing that would be worse
 * than a missing banner is a banner nobody believes.
 */

/** What the banner needs to know, and where it caches it. */
let cached: Promise<OfferPaymentStatus> | null = null;

/**
 * Ask the server once per page load, not once per surface.
 *
 * Three surfaces mount this component and two of them can be on screen at the
 * same time (the offers pane behind a dialog, the inbox in a drawer). The
 * verdict cannot change without a restart, so a module-level promise is exactly
 * as fresh as the answer can be.
 */
export function loadOfferPaymentStatus(): Promise<OfferPaymentStatus> {
  if (!cached) {
    cached = foyerApi.getOfferPaymentStatus().catch((err) => {
      // Let the next mount try again rather than caching a blip for ever.
      cached = null;
      throw err;
    });
  }
  return cached;
}

/** Test-only: forget the cached verdict. */
export function resetOfferPaymentStatusCache(): void {
  cached = null;
}

/**
 * The verdict, or null while it is unknown. Exported because POS-12's revenue
 * dashboard has a second reason to want it: a revenue figure produced by
 * imaginary money should be labelled in the export too, not only on screen.
 */
export function useOfferPaymentStatus(): OfferPaymentStatus | null {
  const [status, setStatus] = useState<OfferPaymentStatus | null>(null);
  useEffect(() => {
    let live = true;
    loadOfferPaymentStatus()
      .then((s) => {
        if (live) setStatus(s);
      })
      .catch(() => {
        // Silent by design - see the header.
      });
    return () => {
      live = false;
    };
  }, []);
  return status;
}

export interface DemoPaymentsBannerProps {
  /** Extra bottom margin, for surfaces that stack their own spacing. */
  sx?: object;
}

export default function DemoPaymentsBanner({
  sx,
}: DemoPaymentsBannerProps = {}) {
  const status = useOfferPaymentStatus();
  if (!status) return null;

  const { folio, checkout } = status;
  const demo = folio.demo || checkout.demo;

  // Real money, real provider, and it can actually post. Nothing to warn about.
  if (folio.configured && folio.operational && !demo) return null;

  return (
    <Box sx={{ mb: 2, ...sx }}>
      <Alert severity={demo ? "warning" : "info"} variant="filled">
        <AlertTitle sx={{ fontWeight: 700 }}>
          {demo ? "Demo payments" : "Charges are not being posted"}
        </AlertTitle>
        {demo ? (
          <Typography variant="body2">
            Every charge from a television is accepted automatically and written
            as a folio line in Foyer only — <strong>no money moves</strong> and
            no PMS is contacted. Do not serve a paying guest with this.
            {folio.provider ? ` (provider: ${folio.provider})` : ""}
          </Typography>
        ) : (
          <Typography variant="body2">
            An accepted request stays <strong>unpaid</strong> and nothing
            reaches a folio.
            {folio.reason ? ` ${folio.reason}.` : ""}
          </Typography>
        )}
      </Alert>
    </Box>
  );
}
